import { useState } from 'react';
import type { MemberUnavailability } from '../../../types/event';

interface Props {
  items: MemberUnavailability[];
  onAdd: (startDate: string, endDate: string, reason: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export default function UnavailabilityManager({ items, onAdd, onDelete }: Props) {
  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onAdd(startDate, endDate, reason);
      setStartDate('');
      setEndDate('');
      setReason('');
      setShowForm(false);
    } catch {
      setError('Falha ao salvar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fmt = (d: string) =>
    new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Minhas Indisponibilidades</h3>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="text-xs text-[#C8A96E] hover:text-[#D4B87A]"
        >
          + Adicionar
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="bg-white/[0.03] border border-white/10 rounded-xl p-4 space-y-3">
          {error && <p className="text-xs text-red-400">{error}</p>}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs text-white/40 mb-1">De</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#C8A96E]/50"
                required
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-white/40 mb-1">Até</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#C8A96E]/50"
                required
              />
            </div>
          </div>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motivo (opcional)"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#C8A96E]/50"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="flex-1 py-2 border border-white/10 text-white/50 rounded-lg text-xs hover:text-white"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2 bg-[#C8A96E] text-[#0A0A0F] font-semibold rounded-lg text-xs disabled:opacity-50"
            >
              Salvar
            </button>
          </div>
        </form>
      )}

      {items.length === 0 && !showForm && (
        <p className="text-xs text-white/30 py-6 text-center">Sem indisponibilidades cadastradas</p>
      )}

      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3"
          >
            <div>
              <p className="text-sm text-white">
                {fmt(item.start_date)} → {fmt(item.end_date)}
              </p>
              {item.reason && <p className="text-xs text-white/40 mt-0.5">{item.reason}</p>}
            </div>
            <button
              onClick={() => onDelete(item.id)}
              className="text-xs text-white/30 hover:text-red-400 transition-colors"
            >
              <i className="ri-delete-bin-line" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
